import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Spin, Button, Space, Alert } from 'antd';
import { ToolOutlined, ArrowLeftOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { LinkedAgentsPanel } from './LinkedAgentsPanel';
import { SkillEditorDrawer } from './SkillEditorDrawer';

const CATEGORY_TONE: Record<string, PillTone> = {
  Clinical: 'ink',
  Protocol: 'violet',
  Operational: 'amber',
  Communication: 'green',
};

export const SkillDetailPage = observer(function SkillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : null;
  const { skillStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  useEffect(() => {
    if (numericId != null) void store.loadDetail(numericId);
  }, [numericId, store]);

  if (numericId == null || Number.isNaN(numericId)) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<ToolOutlined />}
          title="Invalid skill id"
          description="Pick a skill from the library."
          action={
            <Link to="/skills">
              <Button type="primary">All skills</Button>
            </Link>
          }
        />
      </SectionCard>
    );
  }

  const skill = store.detailCache.get(numericId) ?? null;
  const isLoading = store.detailLoadingId === numericId && !skill;

  // Smart-edit drawer — same SkillEditorDrawer used from the Skills list.
  // Opens with this skill loaded; on save we just refresh the detail cache.
  const [editorOpen, setEditorOpen] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Space size={4}>
          <Link to="/skills">
            <Button type="text" size="small" icon={<ArrowLeftOutlined />}>
              All skills
            </Button>
          </Link>
          {skill && (
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              loading={store.detailLoadingId === numericId}
              onClick={() => void store.loadDetail(numericId, true)}
            >
              Refresh
            </Button>
          )}
        </Space>
        {skill && (
          <Button type="primary" icon={<EditOutlined />} onClick={() => setEditorOpen(true)}>
            Edit with AI co-pilot
          </Button>
        )}
      </div>

      {isLoading && (
        <SectionCard>
          <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        </SectionCard>
      )}

      {!isLoading && !skill && (
        <SectionCard>
          <BrandedEmpty
            icon={<ToolOutlined />}
            title="Skill not found"
            description="It may have been retired. Check the library for current skills."
          />
        </SectionCard>
      )}

      {skill && (
        <>
          <SectionCardHero
            title={skill.name}
            description={skill.description}
            accent="violet"
            isDark={isDark}
            pills={[
              skill.category ? {
                tone: CATEGORY_TONE[skill.category] ?? 'slate' as PillTone,
                label: skill.category,
              } : { tone: 'slate' as PillTone, label: 'Uncategorized' },
              skill.version
                ? { tone: 'slate' as PillTone, label: `v${skill.version}` }
                : null,
              skill.isActive === false
                ? { tone: 'slate' as PillTone, label: 'Inactive' }
                : { tone: 'green' as PillTone, label: 'Active' },
              { tone: 'slate' as PillTone, label: `#${skill.id}` },
            ].filter(Boolean) as { tone: PillTone; label: string }[]}
          />

          {skill.instructions ? (
            <SectionCard
              title="Instructions"
              subtitle="Markdown body loaded by the agent on demand. Edit support arrives in H2."
            >
              <div
                style={{
                  background: isDark ? colors.slate[900] : colors.slate[50],
                  border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                  borderRadius: 8,
                  padding: 16,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12.5,
                  lineHeight: 1.65,
                  color: isDark ? colors.slate[100] : colors.slate[800],
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 640,
                  overflow: 'auto',
                }}
              >
                {skill.instructions}
              </div>
            </SectionCard>
          ) : (
            <SectionCard title="Instructions">
              <BrandedEmpty
                icon={<ToolOutlined />}
                title="No instructions on this row"
                description="The skill metadata is present but the markdown body wasn't returned by the API."
              />
            </SectionCard>
          )}

          <LinkedAgentsPanel skillId={skill.id} skillName={skill.name} />

          <SkillEditorDrawer
            skillId={editorOpen ? skill.id : null}
            preview={skill}
            onClose={() => setEditorOpen(false)}
            onSaved={() => void store.loadDetail(skill.id, true)}
          />

          {(skill.alwaysLoad === true) && (
            <Alert
              type="info"
              showIcon
              message="Always-load procedure"
              description="Agents linked to this procedure load it on every turn (skill router bypassed). Use sparingly — it consumes prompt tokens on every request."
            />
          )}
        </>
      )}
    </div>
  );
});

// ── Hero ─────────────────────────────────────────────────────────────────────

function SectionCardHero({
  title,
  description,
  accent,
  pills,
  isDark,
}: {
  title: string;
  description?: string;
  accent: 'ink' | 'violet';
  isDark: boolean;
  pills: { tone: PillTone; label: string }[];
}) {
  const heroBg = isDark
    ? `linear-gradient(135deg, ${colors[accent][700]}40 0%, ${colors.slate[800]} 100%)`
    : `linear-gradient(135deg, ${colors[accent][50]} 0%, #FFFFFF 100%)`;

  return (
    <div
      style={{
        padding: '22px 24px',
        borderRadius: 16,
        background: heroBg,
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 180,
          height: 180,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors[accent][200] ?? colors[accent][100]}55 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: isDark ? colors.slate[50] : colors.slate[900],
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {title}
        </div>
        {description && (
          <div
            style={{
              fontSize: 13.5,
              color: isDark ? colors.slate[300] : colors.slate[600],
              marginTop: 4,
              lineHeight: 1.55,
              maxWidth: 720,
            }}
          >
            {description}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {pills.map((p) => (
            <StatusPill key={p.label} tone={p.tone}>{p.label}</StatusPill>
          ))}
        </div>
      </div>
    </div>
  );
}
